import React, { Dispatch, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import DialogHeader from '../_parts/DialogHeader';
import { useDispatch, useSelector } from 'react-redux';
import { IAppReduxStore } from '../../../../redux/store';
import DialogFrame from '../_parts/DialogFrame';

import { useParams } from 'react-router-dom';

import { DialogContent } from '@mui/material';
import { ContentLayout } from '../_parts/ContentLayout';
import Documents from '../_parts/Documents';
import { removeProjectDocumentsInfoItem } from '../../../../redux/project/documents/sliceProjectDocuments';
import NewAssemblyIndexContent from './_parts/NewAssemblyIndexContent';
import NewAssemblyIndexActions from './_parts/NewAssemblyIndexActions';
import {
    clearNewIndexPartialModels,
    closeNewAssemblyIndexDialog,
    getNewAssemblyIndexPartialModels,
    resetNewIndexAssemblySuccess,
} from '../../../../redux/project/documents/sliceProjectNewAssemblyIndex';
import { ToastSeverity } from '../../Toast/Toast';
import { useEnqueueSnackbar } from '../../../../hooks/useEnqueueSnackbar';
import { fetchDocuments } from '../../../../util/ProjectUtils';
import {
    fetchImportViews,
    fetchImportViewData,
    fetchImportDocTypes,
} from '../../../../util/ImportUtils';

const initialMainState: INewAssemblyIndexMainState = {
    nextIndex: '',
    subject: '',
    selectedPartialModelIds: [],
};

const initialErrorState: INewAssemblyIndexErrorState = {
    selectedPartialModelsError: false,
    nextIndexError: false,
};

export default () => {
    const { t } = useTranslation('translations', { keyPrefix: 'newAssemblyIndexDialog' });

    const params = useParams();

    const { replicaId, areaName } = params;

    const dispatch: Dispatch<unknown> = useDispatch();

    const enqueueSnackbar = useEnqueueSnackbar();

    const { data: documents, loading: loadingInfoDocs } = useSelector(
        (_store: IAppReduxStore) => _store.projectDocuments.info,
    );

    const {
        isDialogOpen,
        loading: loadingNewAssemblyIndex,
        data: newAssemblyIndexData,
        selectedAssemblyId,
        confirmNewIndexPartialModels,
        newIndexAssemblySuccess,
    } = useSelector((_store: IAppReduxStore) => _store.projectNewAssemblyIndex);

    const [mainState, setMainState] = useState<INewAssemblyIndexMainState>(initialMainState);
    const [showErrorState, setShowErrorState] =
        useState<INewAssemblyIndexErrorState>(initialErrorState);

    useEffect(() => {
        if (newAssemblyIndexData) {
            handleChange({ field: 'nextIndex', value: newAssemblyIndexData.nextIndex });
            handleChange({ field: 'subject', value: newAssemblyIndexData.subject });

            setShowErrorState(prevState => ({
                ...prevState,
                nextIndexError: !newAssemblyIndexData.nextIndex,
            }));
        }
    }, [newAssemblyIndexData]);

    useEffect(() => {
        dispatch(
            getNewAssemblyIndexPartialModels({
                replicaId,
                universalId: selectedAssemblyId,
            }),
        );
    }, []);

    useEffect(() => {
        if (newIndexAssemblySuccess) {
            enqueueSnackbar(t(`toastSuccess`), {
                severity: ToastSeverity.Success,
            });
            if (areaName === 'import') {
                fetchImportViews(replicaId);
                fetchImportViewData(replicaId);
                fetchImportDocTypes(replicaId);
            } else {
                fetchDocuments(params);
            }
            onClose();
        } else if (newIndexAssemblySuccess === false) {
            enqueueSnackbar(t(`toastError`), {
                severity: ToastSeverity.Error,
            });
        }

        dispatch(resetNewIndexAssemblySuccess());
    }, [newIndexAssemblySuccess]);

    const handleChange = ({ field, value }: { field: string; value: string | number }) => {
        setShowErrorState({ ...showErrorState, [field]: false });
        setMainState(prevState => ({
            ...prevState,
            [field]: value,
        }));
    };

    const resetState = () => {
        setShowErrorState({ ...initialErrorState });
        setMainState({ ...initialMainState });
    };

    const onClose = () => {
        resetState();

        dispatch(clearNewIndexPartialModels());
        dispatch(closeNewAssemblyIndexDialog());
    };

    const removeDocument = (universalId: string) => {
        dispatch(removeProjectDocumentsInfoItem({ universalId }));
    };

    return (
        <DialogFrame open={isDialogOpen}>
            <DialogHeader onClose={onClose} dialogTitle={t(`dialogTitle`)} />
            <DialogContent sx={{ pt: 0, overflowX: 'hidden' }}>
                <ContentLayout
                    loading={
                        loadingInfoDocs ||
                        loadingNewAssemblyIndex ||
                        confirmNewIndexPartialModels.loading
                    }
                    documentSelection={
                        <Documents removeDocument={removeDocument} documents={documents} />
                    }
                    main={
                        <NewAssemblyIndexContent
                            mainState={mainState}
                            handleChange={handleChange}
                            showErrorState={showErrorState}
                        />
                    }
                />
            </DialogContent>

            <NewAssemblyIndexActions
                setShowErrorState={setShowErrorState}
                mainState={mainState}
                onClose={onClose}
            />
        </DialogFrame>
    );
};

export interface INewAssemblyIndexMainState {
    nextIndex: string;
    subject: string;
    selectedPartialModelIds: string[];
}

export interface INewAssemblyIndexErrorState {
    selectedPartialModelsError: boolean;
    nextIndexError: boolean;
}
